/**
 * DevPanelComponent (P1-G4 + P1-G11): role switcher + Slow AI toggle.
 *
 * Visible only when environment.useMockData === true. Production builds
 * never bundle this component (we still guard with an *ngIf for safety).
 */

import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-dev-panel',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    @if (mockMode) {
      <div class="dev-panel">
        <button class="dev-panel__fab" (click)="open.set(!open())" type="button" aria-label="Dev panel">
          <wg-icon name="gear" [size]="18" />
        </button>
        @if (open()) {
          <div class="dev-panel__body card fade-up">
            <div class="dev-panel__title">Mock mode controls</div>
            <div class="dev-panel__row">
              <label>Role</label>
              <div class="dev-panel__pillgroup">
                <button [class.active]="currentRole() === 'developer'" (click)="setRole('developer')" type="button">developer</button>
                <button [class.active]="currentRole() === 'admin'" (click)="setRole('admin')" type="button">admin</button>
              </div>
            </div>
            <div class="dev-panel__row">
              <label>Slow AI</label>
              <button class="dev-panel__toggle-btn" [class.on]="slowAi()" (click)="toggleSlowAi()" type="button">
                {{ slowAi() ? 'ON (12s delay)' : 'OFF (800ms)' }}
              </button>
            </div>
            <div class="dev-panel__hints">
              <div class="dev-panel__hints-title">URL hints active:</div>
              <code>{{ urlHints }}</code>
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    .dev-panel {
      position: fixed;
      bottom: 16px;
      right: 16px;
      z-index: 90;
    }
    .dev-panel__fab {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: var(--text);
      color: var(--bg);
      border: none;
      box-shadow: var(--shadow-2);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.15s;
    }
    .dev-panel__fab:hover { transform: scale(1.06); }

    .dev-panel__body {
      position: absolute;
      bottom: 52px;
      right: 0;
      width: 280px;
      padding: 14px;
      box-shadow: var(--shadow-2);
    }
    .dev-panel__title {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--text-3);
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border);
      margin-bottom: 10px;
    }
    .dev-panel__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    .dev-panel__row label {
      font-size: 13px;
      font-weight: 500;
    }
    .dev-panel__pillgroup { display: flex; gap: 4px; }
    .dev-panel__pillgroup button {
      padding: 4px 10px;
      border: 1px solid var(--border-strong);
      background: var(--surface);
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      color: var(--text-2);
      transition: all 0.15s;
    }
    .dev-panel__pillgroup button.active {
      background: var(--accent);
      color: #fff;
      border-color: var(--accent);
    }
    .dev-panel__toggle-btn {
      padding: 4px 12px;
      border-radius: 6px;
      border: 1px solid var(--border-strong);
      background: var(--surface);
      font-size: 12px;
      font-weight: 600;
      color: var(--text-2);
    }
    .dev-panel__toggle-btn.on {
      background: var(--amber-soft);
      color: var(--amber);
      border-color: var(--amber);
    }
    .dev-panel__hints {
      margin-top: 10px;
      padding-top: 10px;
      border-top: 1px solid var(--border);
    }
    .dev-panel__hints-title {
      font-size: 11px;
      color: var(--text-3);
      margin-bottom: 4px;
    }
    .dev-panel__hints code {
      font-size: 11.5px;
      font-family: var(--font-mono);
      color: var(--text-2);
      background: var(--surface-2);
      padding: 4px 8px;
      border-radius: 4px;
      display: block;
      word-break: break-all;
    }
  `],
})
export class DevPanelComponent {
  readonly mockMode = environment.useMockData;
  readonly open = signal<boolean>(false);
  readonly currentRole = signal<string>(sessionStorage.getItem('current-role') ?? 'developer');
  readonly slowAi = signal<boolean>(sessionStorage.getItem('mock-slow-ai') === 'true');

  setRole(role: 'developer' | 'admin'): void {
    sessionStorage.setItem('current-role', role);
    this.currentRole.set(role);
    // Trigger a reload so the shell + guards pick up the new role.
    window.location.reload();
  }

  toggleSlowAi(): void {
    const next = !this.slowAi();
    sessionStorage.setItem('mock-slow-ai', String(next));
    this.slowAi.set(next);
  }

  get urlHints(): string {
    const params = new URLSearchParams(window.location.search);
    const collected: string[] = [];
    for (const key of ['retry', 'smoke', 'ai']) {
      const v = params.get(key);
      if (v) collected.push(`?${key}=${v}`);
    }
    return collected.length ? collected.join(' ') : '(none — append ?retry=fail, ?smoke=timeout, ?smoke=tool-fail, ?ai=slow)';
  }
}
